import { useId, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSignInAgain } from "../hooks/useSignInAgain";

interface StaleSessionNoticeProps {
  returnTo: string;
  id?: string;
  className?: string;
  body?: string;
  emphasis?: "primary" | "secondary";
  fullWidth?: boolean;
  autoFocus?: boolean;
  onSignInAgain?: () => void;
}

const BUTTON_STYLES = {
  primary: "bg-indigo text-white hover:bg-indigo-bright",
  secondary: "border border-border bg-surface text-heading hover:border-border-light",
};

export function StaleSessionNotice({
  returnTo,
  id,
  className = "",
  body,
  emphasis = "primary",
  fullWidth = false,
  autoFocus = false,
  onSignInAgain,
}: StaleSessionNoticeProps) {
  const { t } = useTranslation();
  const defaultSignInAgain = useSignInAgain(returnTo);
  const signInAgain = onSignInAgain ?? defaultSignInAgain;
  const generatedId = useId();
  const bodyId = id ?? generatedId;
  const buttonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!autoFocus) return;
    buttonRef.current?.focus();
  }, [autoFocus]);

  return (
    <div className={className}>
      <p id={bodyId} className="text-sm text-muted">
        {body ?? t("account.staleSession.body")}
      </p>
      <button
        ref={buttonRef}
        type="button"
        onClick={signInAgain}
        autoFocus={autoFocus}
        data-autofocus={autoFocus || undefined}
        aria-describedby={bodyId}
        className={`mt-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${BUTTON_STYLES[emphasis]} ${fullWidth ? "w-full" : ""}`}
      >
        {t("account.staleSession.action")}
      </button>
    </div>
  );
}
